import React, { useState } from 'react';
import './RsvpForm.css';
import API_URL from '../apiUrl';

const RsvpForm = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    attending: '',
    numberOfGuests: '1',
    events: [],
    dietaryRestrictions: ''
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEventChange = (e) => {
    const { value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      events: checked 
        ? [...prev.events, value]
        : prev.events.filter(event => event !== value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.email.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    if (!formData.attending) {
      setError('Please select if you will be attending');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_URL}/api/rsvp/`, {
        method: 'POST',
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          attending: formData.attending,
          number_of_guests: parseInt(formData.numberOfGuests),
          events: formData.events,
          dietary_restrictions: formData.dietaryRestrictions
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSuccess(result.message);
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          attending: '',
          numberOfGuests: '1',
          events: [],
          dietaryRestrictions: ''
        });
      } else {
        setError(result.message || 'Failed to submit RSVP. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please try again later.');
    }

    setLoading(false);
  };

  return (
    <div className="rsvp-page">
      <div className="rsvp-hero-section">
        <div className="rsvp-container">
          <div className="rsvp-content">
            <h1 className="rsvp-title">RSVP</h1>
            <p className="rsvp-subtitle">Please let us know if you'll be joining us for our special day</p>
            
            <div className="rsvp-form-container">
              <form className="rsvp-form" onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="firstName">First Name</label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="lastName">Last Name</label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="attending">Will you be attending?</label>
                  <select
                    id="attending"
                    name="attending"
                    value={formData.attending}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Please select...</option>
                    <option value="yes">Yes, I'll be there</option>
                    <option value="no">No, I can't make it</option>
                  </select>
                </div>

                {formData.attending === 'yes' && (
                  <>
                    <div className="form-group">
                      <label htmlFor="numberOfGuests">Number of Guests</label>
                      <select
                        id="numberOfGuests"
                        name="numberOfGuests"
                        value={formData.numberOfGuests}
                        onChange={handleInputChange}
                      >
                        {[...Array(10)].map((_, i) => (
                          <option key={i + 1} value={i + 1}>{i + 1}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group" style={{display :'none'}}>
                      <label>Which events will you attend?</label>
                      <div className="checkbox-group">
                        <label className="checkbox-item">
                          <input
                            type="checkbox"
                            value="welcome-party"
                            checked={formData.events.includes('welcome-party')}
                            onChange={handleEventChange}
                          />
                          <span className="checkmark"></span>
                          Welcome Party (Sept 20)
                        </label>
                        
                        <label className="checkbox-item">
                          <input
                            type="checkbox"
                            value="ceremony-reception"
                            checked={formData.events.includes('ceremony-reception')}
                            onChange={handleEventChange}
                          />
                          <span className="checkmark"></span>
                          Ceremony & Reception (Sept 21)
                        </label>
                        
                        <label className="checkbox-item">
                          <input
                            type="checkbox"
                            value="farewell-brunch"
                            checked={formData.events.includes('farewell-brunch')}
                            onChange={handleEventChange}
                          />
                          <span className="checkmark"></span>
                          Farewell Brunch (Sept 22)
                        </label>
                      </div>
                    </div>

                    <div className="form-group" style={{display :'none'}}>
                      <label htmlFor="dietaryRestrictions">Dietary Restrictions</label>
                      <textarea
                        id="dietaryRestrictions"
                        name="dietaryRestrictions"
                        value={formData.dietaryRestrictions}
                        onChange={handleInputChange}
                        placeholder="Please let us know of any dietary restrictions"
                        rows={3}
                      />
                    </div>
                  </>
                )}

                {error && (
                  <div className="error-message">
                    {error}
                  </div>
                )}

                {success && (
                  <div className="success-message">
                    {success}
                  </div>
                )}

                <button 
                  type="submit" 
                  className="rsvp-submit-btn"
                  disabled={loading}
                >
                  {loading ? 'Sending RSVP...' : 'Send RSVP'}
                </button>
              </form>
            </div>

            <div className="rsvp-footer">
              <p>Please RSVP by July 16, 2025</p>
              <p>Questions? Contact us at <a href="mailto:info@ren4misenate.com">info@ren4misenate.com</a></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RsvpForm;
